import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { CallButtons } from "@/components/CallButtons";
import { breadcrumbSchema, faqPageSchema, legalServiceSchema, attorneySchema } from "@/lib/schemas";
import { trackAuthorityPageView, trackPriorityFaqOpen } from "@/lib/analytics";
import { ChevronDown, Star } from "lucide-react";

const PATH = "/estate-planning-frisco-plano";
const SLUG = "estate-planning-frisco-plano";

const SERVICES = [
  ["Wills", "Clear instructions for property, guardians for minor children, and the executor you choose."],
  ["Revocable living trusts", "Trusts drafted for your family, plus the follow-through to fund them: deeds, account registrations, and assignments."],
  ["Powers of attorney", "Statutory durable and medical powers of attorney so a trusted person can act if you cannot."],
  ["Health-care directives", "Directive to physicians and HIPAA authorizations that make your wishes known."],
  ["Beneficiary review", "Retirement accounts, life insurance, and payable-on-death accounts checked against the overall plan."],
  ["Business-owner planning", "Coordinating your estate plan with company agreements, buy-sell terms, and succession."],
];

const FAQ = [
  ["Do I need a will or a living trust?", "It depends on your property, your family, and your goals. Many Texas families use a will; others benefit from a revocable living trust, particularly with property in more than one state, privacy concerns, or a blended family. We review your situation and explain the trade-offs before recommending either."],
  ["I already signed a trust. Is my planning finished?", "Not necessarily. A trust generally controls only the assets that are actually titled to it or that pass to it by beneficiary designation. A funding review compares your deeds, account registrations, and beneficiary designations with the plan you signed."],
  ["Do you meet with clients from Plano, McKinney, and Allen?", "Yes. Our principal office at 2591 Dallas Parkway, Suite 300, in Frisco serves families across Collin and Denton Counties, and meetings can also be arranged by video."],
  ["How quickly will I hear back?", "We’ll call you back the same day or within 24 hours. You may also call (972) 712-1515 or toll-free (888) 517-4575."],
];

function FaqList() {
  const [open, setOpen] = useState(null);
  const toggle = (i, q) => {
    if (open !== i) trackPriorityFaqOpen({ page_slug: SLUG, faq_position: i + 1, faq_question: q });
    setOpen(open === i ? null : i);
  };
  return (
    <ul className="mt-12 space-y-6" data-testid="efp-faq-list">
      {FAQ.map(([q, a], i) => (
        <li key={q} className="hairline-top pt-5">
          <button className="w-full flex items-start justify-between gap-6 text-left group" onClick={() => toggle(i, q)} aria-expanded={open === i} data-testid={`efp-faq-toggle-${i}`}>
            <span className="font-display text-lg md:text-2xl text-navy group-hover:text-oxblood transition">{q}</span>
            <ChevronDown size={20} className={`text-brass transition-transform mt-1 ${open === i ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
          {open === i && <p className="mt-4 text-slate2 font-serif leading-relaxed" data-testid={`efp-faq-answer-${i}`}>{a}</p>}
        </li>
      ))}
    </ul>
  );
}

export default function EstatePlanningFriscoPlano() {
  useEffect(() => {
    trackAuthorityPageView({ page_slug: SLUG, page_title: "Estate Planning Attorney in Frisco & Plano", priority_faq_count: FAQ.length });
  }, []);

  return (
    <div className="bg-ivory" data-testid="estate-frisco-plano-page">
      <SEO
        title="Estate Planning Attorney in Frisco & Plano, TX | Lone Star Counsel"
        description="Wills, living trusts, powers of attorney, and trust-funding reviews for Frisco, Plano, McKinney, and Allen families. Call (972) 712-1515 or toll-free (888) 517-4575."
        path={PATH}
        schema={[
          legalServiceSchema({ name: "Estate Planning — Frisco & Plano", description: "Wills, trusts, powers of attorney, and trust-funding reviews for Collin and Denton County families.", url: PATH, area: "Frisco, Plano, McKinney, Allen, Texas" }),
          attorneySchema(),
          faqPageSchema(FAQ.map(([q, a]) => ({ q, a }))),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Estate Planning", url: "/texas-estate-planning-attorney/" },
            { name: "Frisco & Plano", url: PATH },
          ]),
        ]}
      />

      <section className="max-w-[1200px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16" data-testid="efp-hero">
        <div className="kicker">Estate Planning &middot; Frisco &amp; Plano</div>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-navy" data-testid="efp-h1">
          Estate Planning for<br /><span className="italic text-oxblood">Frisco &amp; Plano Families.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-lg md:text-xl text-ink font-serif leading-relaxed">
          Wills, living trusts, powers of attorney, and the follow-through that makes them work &mdash; from our principal office on Dallas Parkway in Frisco, serving Plano, McKinney, Allen, and Prosper.
        </p>
        <CallButtons source="efp-hero" className="mt-10 max-w-2xl" />
        <Link to="/intake" className="mt-6 inline-block text-navy font-semibold underline underline-offset-4 hover:text-oxblood" data-testid="efp-hero-intake">
          Or request a consultation online &rarr;
        </Link>
      </section>

      <section className="bg-linen py-20 md:py-24" data-testid="efp-services">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">What We Prepare</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.0] text-navy">A complete, coordinated plan.</h2>
          <dl className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
            {SERVICES.map(([t, d], i) => (
              <div key={t} className="hairline-top pt-5" data-testid={`efp-service-${i}`}>
                <dt className="font-display text-xl md:text-2xl text-navy flex items-center gap-3"><Star size={14} className="text-brass" aria-hidden="true" />{t}</dt>
                <dd className="mt-2 text-slate2 font-serif leading-relaxed">{d}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-12 font-serif text-lg text-ink">
            Already signed a trust? Read <Link to="/journal/living-trust-funding-review" className="text-oxblood underline underline-offset-4" data-testid="efp-article-link">You Created a Living Trust&mdash;But Did You Fund It?</Link>
          </p>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="efp-faqs">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Frequently Asked</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.0] text-navy">Estate planning questions.</h2>
          <FaqList />
        </div>
      </section>

      <section className="panel-navy py-20 md:py-24" data-testid="efp-contact">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12 text-center">
          <div className="kicker">Lone Star &#9733; Counsel</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl text-ivory leading-[1.0]">Start your plan this week.</h2>
          <p className="mt-6 text-ivory/80 font-serif text-lg">2591 Dallas Parkway, Suite 300, Frisco, Texas 75034</p>
          <div className="mt-10 bg-ivory p-6 md:p-8 max-w-2xl mx-auto">
            <CallButtons source="efp-footer" center />
          </div>
          <p className="mt-8 text-ivory/60 font-serif text-sm">LONE STAR &#9733; COUNSEL is a dba of Pratt Law Group, PLLC. General information, not legal advice. <Link to="/legal" className="underline">Legal disclosures</Link>.</p>
        </div>
      </section>
    </div>
  );
}
